import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useUserFavoritesContext } from '../contexts/UserFavoritesContext';
import { UserFavorite } from '../types/userFavorites';
import { getKindDisplayName } from '../utils/urlUtils';
import { useTrackedLinkModal } from '../hooks';
import { ContentModal } from '../components/ui';
import { ResourceHistory } from '../types/userActivity';
import { getEffectiveSortDate } from '../utils/favoritesUtils';
import { FavoriteResourceCard } from '../components/userFavorites/FavoriteResourceCard';
import {
  Heart,
  Search,
  Filter,
  AlertCircle,
  RefreshCw,
  X,
  ArrowUpDown,
} from 'lucide-react';

type KindFilter = 'all' | 'video' | 'audio' | 'recipe' | 'article' | 'meeting' | 'manual' | 'chat' | 'activity';
type SortOption = 'lastAccessed' | 'mostVisited' | 'mostTime' | 'recentlyAdded';

const SORT_LABELS: Record<SortOption, string> = {
  lastAccessed: 'Last Accessed',
  mostVisited: 'Most Visited',
  mostTime: 'Most Time Spent',
  recentlyAdded: 'Recently Added',
};

const FavoritesPage: React.FC = () => {
  const {
    favorites,
    loading,
    loadingMore,
    error,
    hasMore,
    toggleFavoriteFlag,
    deleteFavorite,
    loadMore,
    refresh,
  } = useUserFavoritesContext();

  const { isOpen, url, title, allowCredentials, trustDomain, openLink, closeModal } = useTrackedLinkModal();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [sortBy, setSortBy] = useState<SortOption>('recentlyAdded');
  const [togglingFlagId, setTogglingFlagId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const [onlyManual, setOnlyManual] = useState(false);

  // Get unique kinds from favorites for filter tabs
  const availableKinds = useMemo(() => {
    const kinds = new Set(favorites.map(f => f.kind));
    return Array.from(kinds);
  }, [favorites]);

  const hasManualItems = useMemo(() => favorites.some(f => f.payload?.metadata?.isFavorited === true), [favorites]);
  const effectiveOnlyManual = onlyManual && hasManualItems;

  // Filter favorites based on search, kind, and favorites-only toggle
  const filteredFavorites = useMemo(() => {
    return favorites.filter(item => {
      const matchesKind = kindFilter === 'all' || item.kind === kindFilter;
      const matchesSearch = !searchQuery || 
        item.payload.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.payload.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesManual = !effectiveOnlyManual || item.payload?.metadata?.isFavorited === true;
      // Hide video resources that have no thumbnail image
      const hasRequiredThumbnail = item.kind !== 'video' || !!item.payload.thumbnail;
      return matchesKind && matchesSearch && matchesManual && hasRequiredThumbnail;
    });
  }, [favorites, kindFilter, searchQuery, effectiveOnlyManual]);

  // Sorted resources for the Resources tab (flat list)
  const sortedResources = useMemo(() => {
    const items = [...filteredFavorites];
    switch (sortBy) {
      case 'lastAccessed':
        return items.sort((a, b) => getEffectiveSortDate(b) - getEffectiveSortDate(a));
      case 'mostVisited': {
        const getCount = (f: UserFavorite) => (f.payload?.metadata?.history as ResourceHistory | undefined)?.sessionCount || 0;
        return items.sort((a, b) => getCount(b) - getCount(a));
      }
      case 'mostTime': {
        const getTime = (f: UserFavorite) => (f.payload?.metadata?.history as ResourceHistory | undefined)?.totalTimeMs || 0;
        return items.sort((a, b) => getTime(b) - getTime(a));
      }
      case 'recentlyAdded':
        return items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      default:
        return items;
    }
  }, [filteredFavorites, sortBy]);

  // Toggle isFavorited flag handler
  const handleToggleFlag = useCallback(async (itemId: string) => {
    setTogglingFlagId(itemId);
    try {
      await toggleFavoriteFlag(itemId);
    } catch (err) {
      console.error('Failed to toggle favorite flag:', err);
    } finally {
      setTogglingFlagId(null);
    }
  }, [toggleFavoriteFlag]);

  // Delete handler for non-favorited items
  const handleDelete = useCallback(async (itemId: string) => {
    setDeletingItemId(itemId);
    try {
      await deleteFavorite(itemId);
    } catch (err) {
      console.error('Failed to delete item:', err);
    } finally {
      setDeletingItemId(null);
      setConfirmingDeleteId(null);
    }
  }, [deleteFavorite]);

  const filterTabs: { key: KindFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    ...availableKinds.map(kind => ({
      key: kind as KindFilter,
      label: getKindDisplayName(kind as any) || kind
    }))
  ];

  return (
    <div className="h-full flex flex-col bg-rm-bg-0">
      {/* Header */}
      <div className="bg-rm-bg-0 border-b border-rm-neutral-600/20 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-rm-accent-300/20 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-rm-accent-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-rm-primary-700">My Resources</h1>
              <p className="text-sm text-rm-neutral-600">
                {favorites.length} saved item{favorites.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => refresh()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-rm-neutral-600 hover:text-rm-primary-700 hover:bg-rm-bg-50 rounded-card transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Search, Filter, and Sort */}
        {/* Expanded search takes full width */}
        {isSearchOpen && (
          <div className="relative animate-[fadeIn_150ms_ease-out]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-rm-neutral-600" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search favorites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={() => { if (!searchQuery) setIsSearchOpen(false); }}
              className="w-full max-w-md pl-10 pr-9 py-2 border border-rm-neutral-600/30 rounded-card text-sm focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500"
            />
            <button
              onClick={() => { setSearchQuery(''); setIsSearchOpen(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-rm-neutral-600 hover:text-rm-ink-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search icon (collapsed) */}
          {!isSearchOpen && (
            <button
              onClick={() => {
                setIsSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 0);
              }}
              className="p-2 rounded-card border border-rm-neutral-600/30 text-rm-neutral-600 hover:text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
              title="Search favorites"
            >
              <Search className="w-4 h-4" />
            </button>
          )}
            {/* Sort Dropdown */}
              <div className="relative flex items-center gap-1">
                <ArrowUpDown className="w-4 h-4 text-rm-neutral-600" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="appearance-none bg-rm-bg-50 border border-rm-neutral-600/30 rounded-card px-3 py-1.5 text-xs font-medium text-rm-neutral-600 focus:ring-2 focus:ring-rm-accent-500 cursor-pointer pr-6"
                >
                  {(Object.keys(SORT_LABELS) as SortOption[]).map(key => (
                    <option key={key} value={key}>{SORT_LABELS[key]}</option>
                  ))}
                </select>
              </div>

            {/* Only Favorites Toggle */}
            <button
              onClick={() => setOnlyManual(v => !v)}
              disabled={!hasManualItems}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                effectiveOnlyManual
                  ? 'bg-rm-accent-500 text-white'
                  : 'bg-rm-bg-50 text-rm-neutral-600 hover:bg-rm-neutral-600/10'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Only Favorites
            </button>

            {/* Kind Filter Tabs */}
            {filterTabs.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap">
                <Filter className="w-4 h-4 text-rm-neutral-600 mr-1" />
                {filterTabs.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setKindFilter(tab.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      kindFilter === tab.key
                        ? 'bg-rm-primary-700 text-white'
                        : 'bg-rm-bg-50 text-rm-neutral-600 hover:bg-rm-neutral-600/10'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Loading State */}
        {loading && favorites.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-rm-accent-300 border-t-rm-accent-600 rounded-full animate-spin mb-4" />
            <p className="text-sm text-rm-neutral-600">Loading favorites...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-card">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button onClick={() => refresh()} className="mt-2 text-sm text-red-600 hover:underline">
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredFavorites.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 bg-rm-bg-50 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-rm-neutral-600" />
            </div>
            <h3 className="text-lg font-medium text-rm-ink-900 mb-2">
              {searchQuery || kindFilter !== 'all' ? 'No matching favorites' : 'No favorites yet'}
            </h3>
            <p className="text-sm text-rm-neutral-600 text-center max-w-sm">
              {searchQuery || kindFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Start adding favorites by clicking the + icon next to resources in chat conversations.'}
            </p>
            {(searchQuery || kindFilter !== 'all') && (
              <button
                onClick={() => { setSearchQuery(''); setKindFilter('all'); }}
                className="mt-4 text-sm text-rm-accent-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {!loading && !error && sortedResources.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedResources.map(item => (
              <FavoriteResourceCard
                key={item.id}
                item={item}
                onOpenLink={openLink}
                onToggleFlag={handleToggleFlag}
                togglingFlagId={togglingFlagId}
                onConfirmDelete={setConfirmingDeleteId}
                confirmingDeleteId={confirmingDeleteId}
                onDelete={handleDelete}
                deletingItemId={deletingItemId}
                historyItemId={historyItemId}
                onToggleHistory={setHistoryItemId}
              />
            ))}
          </div>
        )}

        {hasMore && filteredFavorites.length > 0 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="px-6 py-2 text-sm font-medium text-rm-primary-700 bg-white border border-rm-neutral-600/20 rounded-card hover:bg-rm-bg-50 transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-rm-accent-300 border-t-rm-accent-600 rounded-full animate-spin" />
                  Loading...
                </span>
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Content Modal */}
      <ContentModal
        url={url}
        title={title}
        isOpen={isOpen}
        onClose={closeModal}
        allowCredentials={allowCredentials}
        trustDomain={trustDomain}
      />
    </div>
  );
};

export default FavoritesPage;
